import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject
} from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { Actions, Store, ofActionSuccessful } from "@ngxs/store";
import { ApplicationState } from "@vp/data-access/application";
import * as JsonSchemaStateActions from "@vp/data-access/json-schema";
import { JsonSchemaState, Schema } from "@vp/data-access/json-schema";
import { OrganizationState } from "@vp/data-access/organization";
import * as TagsActions from "@vp/data-access/tags";
import { UiSchemaState } from "@vp/data-access/ui-schema";
import {
  UiSchemaCollection,
  UiSchemaConfigService,
  UiSchemaLayoutProvider,
  UiSchemaStateProvider
} from "@vp/formly/ui-schema-config";
import { AssignableRoles, Role, Tag, TagType } from "@vp/models";
import { SharedConfirmationService } from "@vp/shared/confirmation";
import { NotificationService } from "@vp/shared/notification-service";
import { filterNullMap } from "@vp/shared/operators";
import { hasOwnProperty, mergeDeep } from "@vp/shared/utilities";
import * as TagManagerActions from "@vp/tag-manager/data-access/tag-manager-state";
import { TagManagerState } from "@vp/tag-manager/data-access/tag-manager-state";
import { AssignableRolesService } from "@vp/user-administration/data-access/user-administration-state";
import { Operation } from "rfc6902";
import { BehaviorSubject, EMPTY, Observable, Subject, combineLatest, of, zip } from "rxjs";
import {
  catchError,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  mergeMap,
  pairwise,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap
} from "rxjs/operators";

@Component({
  selector: "vp-tag-editor",
  templateUrl: "./tag-editor.component.html",
  styleUrls: ["./tag-editor.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TagEditorComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly actions = inject(Actions);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly uiSchemaConfigService = inject(UiSchemaConfigService);
  private readonly uiSchemaLayoutProvider = inject(UiSchemaLayoutProvider);
  private readonly uiSchemaStateProvider = inject(UiSchemaStateProvider);
  private readonly confirmationDialog = inject(SharedConfirmationService);
  private assignableRolesService = inject(AssignableRolesService);
  private notificationService = inject(NotificationService);

  selectedTag$: Observable<Tag | null> = inject(Store).select(TagManagerState.selectedTag);
  uiSchemas$: Observable<UiSchemaCollection[]> = inject(Store).select(UiSchemaState.uiSchemas);
  tagTypes$: Observable<TagType[]> = inject(Store).select(OrganizationState.tagTypes);
  schemas$: Observable<Schema[]> = inject(Store).select(JsonSchemaState.schemas);
  pendingOperations$: Observable<Operation[]> = inject(Store).select(TagManagerState.operations);
  selectedRole$: Observable<Role | null> = inject(Store).select(ApplicationState.selectedRole);

  options: FormlyFormOptions = {
    formState: { disabled: true }
  };
  fields: FormlyFieldConfig[] = [];

  readonly ready$ = new BehaviorSubject<boolean>(false);
  readonly model$ = new BehaviorSubject<Partial<Tag>>({});
  private readonly formGroup$$ = new BehaviorSubject<UntypedFormGroup>(new UntypedFormGroup({}));
  private readonly destroyed$ = new Subject<void>();

  restrictedAccess$ = combineLatest([
    this.selectedTag$,
    this.assignableRolesService.roleWithConditionalAccess$
  ]).pipe(
    takeUntil(this.destroyed$),
    map(([tag, assignableRole]: [Tag | null, AssignableRoles | null]) => {
      if (tag && assignableRole) {
        return !assignableRole.tagEditorReadOnlyFields.tagTypes.includes(tag.tagTypeFriendlyId);
      }
      return true;
    })
  );

  formGroup$ = this.formGroup$$.asObservable();
  isEditing$: Observable<boolean> = this.selectedTag$.pipe(map(tag => !!tag));

  distinctTag$ = this.selectedTag$.pipe(
    takeUntil(this.destroyed$),
    debounceTime(300),
    tap(tag => {
      if (tag === null) this.resetFormWhenTagUnselected();
    }),
    distinctUntilChanged((a, b) => a?.tagId === b?.tagId)
  );

  ngOnInit(): void {
    this.store.dispatch(new JsonSchemaStateActions.GetSchema("tags"));

    /**
     * This is actually subscribing to the execution of the CancelEditingTag action. This does not
     * return the tag. But instead returns an object with details about the action. So we map to the
     * current selected tag to get the current tag id, pull that tag from the tags array in state
     * which should still have the original data, then replace the model with the old data. Effectively
     * "cancelling" the editing of the tag by resetting it to its original state when the form loaded.
     * Not sure if creating a new formGroup was necessary here, but it ensures that the form is truly
     * reset to its original state, regardless of any existing values in the reactive forms model. The
     * tagManager state was already reset in the actual CancelEditingTag action so we do not need to
     * concern ourselves with that here.
     */
    this.actions
      .pipe(
        ofActionSuccessful(TagManagerActions.CancelEditingTag),
        switchMap(() => this.store.selectOnce(TagManagerState.selectedTag)),
        filterNullMap(),
        map(t => t.tagId),
        mergeMap(tagId =>
          this.store
            .selectOnce(TagManagerState.tags)
            .pipe(map(tags => tags.find(t => t.tagId == tagId)))
        ),
        filterNullMap(),
        takeUntil(this.destroyed$)
      )
      .subscribe(tag => {
        this.formGroup$$.next(new UntypedFormGroup({}, { updateOn: "change" }));
        this.model$.next(tag);
      });

    this.distinctTag$
      .pipe(
        filterNullMap(),
        switchMap((tag: Tag) =>
          combineLatest([
            this.tagTypes$.pipe(
              map(tagTypes => tagTypes.find(tt => tt.tagTypeId === tag.tagTypeId)),
              filterNullMap()
            ),
            this.schemas$.pipe(
              map(schemas => schemas.find(s => s.name === "tags")),
              filterNullMap()
            ),
            this.uiSchemas$.pipe(map(uiSchemas => uiSchemas.find(c => c.scope === "tags") ?? null)),
            this.selectedRole$.pipe(
              filterNullMap(),
              map(selectedRole => selectedRole.permissions)
            ),
            of(tag)
          ])
        ),
        concatMap(
          ([tagType, schema, uiSchema, rolePermissions, tag]: [
            TagType,
            Schema,
            UiSchemaCollection | null,
            string[],
            Tag
          ]) => {
            const layout: UiSchemaCollection = uiSchema
              ? mergeDeep(uiSchema, tagType?.tagLayout, "concat")
              : tagType?.tagLayout;

            this.uiSchemaConfigService.addScopedConfig(layout, tagType.friendlyId, "replace");

            /**
             * This is merging the tag generated schema from the actual tags object in the api
             * with the tagSchema in the tagType which defines the tagData so we get the full
             * tag and the tagData schemas as one schema
             */
            const mergedSchema = mergeDeep(
              schema.schema,
              {
                properties: {
                  tagData: {
                    properties: tagType.tagSchema.properties
                  }
                }
              },
              "concat",
              false
            );

            return zip(
              this.uiSchemaLayoutProvider.applyScopes(
                "tag-manager",
                mergedSchema,
                tagType.friendlyId,
                rolePermissions,
                "anyOf"
              ),
              this.uiSchemaStateProvider.applyScopes(
                "tag-manager",
                { formState: { disabled: false } },
                tagType.friendlyId
              ),
              of(tag)
            );
          }
        )
      )
      .subscribe(([schema, formlyFormOptions, tag]) => {
        this.fields = this.uiSchemaLayoutProvider.getFieldConfig(schema);
        this.options = formlyFormOptions;
        /**
         * It is necessary to new up the form group here, because formly retains a reference
         * to the instance and emits old values with new valueChanges emissions when you
         * switch between tags. If the instance is empty formly will automatically rebuild it
         * when the form is re-rendered.
         */
        this.model$.next({ ...tag });
        this.formGroup$$.next(new UntypedFormGroup({}, { updateOn: "change" }));
        this.cdRef.markForCheck(); // necessary to tell formly to re-render the form
      });

    /*
    Every time the distinctTag$ observable emits (when a new tag is selected) we create a new instance of a form group
    which is subscribed to here along with the model, so with each emission of one it waits for the other to emit as well
    which they both should have done un unison in the distinctTag$ chain. We then switchMap to valueChanges on that new
    form group. SwitchMap here will cancel the previous observable and subscribe to the new one. The first emission is from
    the new formgroup which is an empty object, so we just add a filter here to make sure only tags get emitted. We then
    subscribe to the previous AND current values of valueChanges. If the are not equal we know the selected tag changed,
    so we reset the operations, otherwise we know its the previous and current of the same tag so we dispatch to UpdateTag
    which will calculate the Json Patch Operations to add or update to the state. */
    zip(this.formGroup$$, this.model$)
      .pipe(
        takeUntil(this.destroyed$),
        switchMap(([formGroup, partialTag]: [UntypedFormGroup, Partial<Tag>]) => {
          /**
           * This effectively "starts" the observable with the existing tag data otherwise it will wait for two
           * emissions before emitting as the pairwise needs a pair of values to emit.Without this, the previous
           * and current will only emit after the second change, and will not contain the initial diff needed
           * to normalize the data. This writes the proper structure of the tagData if the current value is null,
           * but it will not work if the current value is an empty object because it will try to merge the individual
           * fields into the empty object instead of creating the root tagData structure correctly.
           */
          return formGroup.valueChanges.pipe(
            startWith(partialTag),
            takeUntil(this.destroyed$),
            debounceTime(300)
          );
        }),
        filter((tag: Partial<Tag>) => Object.keys(tag).includes("tagId")),
        pairwise()
      )
      .subscribe({
        next: ([previous, current]: [Partial<Tag>, Partial<Tag>]) => {
          if (current?.tagId && current.tagId === previous?.tagId) {
            if (
              hasOwnProperty(current, "tagData") &&
              current.tagData &&
              Object.keys(<Record<string, unknown>>current.tagData).length === 0
            ) {
              // remove empty tagData
              delete current["tagData"];
            }
            this.store.dispatch(new TagManagerActions.UpdateTag(current.tagId, current));
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  resetFormWhenTagUnselected() {
    this.formGroup$$.next(new UntypedFormGroup({}, { updateOn: "change" }));
    this.model$.next({});
  }

  deleteTag = () => {
    return this.confirmationDialog
      .open(
        "Deleting a tag is permanent. Are you sure you want to continue?",
        "Continue",
        "Deleting Tags",
        "tagManagerDeleteTAg",
        "30vw",
        true,
        true,
        "warn"
      )
      .afterConfirmed()
      .pipe(
        exhaustMap(() => this.selectedTag$),
        map(tag => tag?.tagId),
        filterNullMap(),
        take(1),
        mergeMap(tagId =>
          this.store.dispatch(new TagManagerActions.DeleteTag(tagId)).pipe(
            catchError(error => {
              this.notificationService.errorMessage(error);
              return EMPTY;
            })
          )
        )
      )
      .subscribe(() => {
        this.store.dispatch(new TagsActions.LoadTags(false));
      });
  };
}
